// 列表页骨架：手机（大标题 + 三段 + chips + 卡片）、iPad（列表栏 + 详情分栏）、桌面（表格 + 分页）。
// 版式照订单模块 OrdersPage，抽成可复用的几块，产品 / 套装 / 画册 / 客户 / 供应商共用。建议主会话提取到 src/components。
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { Icon } from '@/components/Icon';
import { Button, IconButton } from '@/components/Button';
import { Empty, ErrorState, Skeleton } from '@/components/States';
import s from './List.module.css';

/* ---------------- 小件 ---------------- */

export function SearchBox({ value, placeholder, onSubmit }: { value: string; placeholder: string; onSubmit: (q: string | undefined) => void }) {
  const [draft, setDraft] = useState(value);
  // URL 里的关键词被别处清掉时（「清空」、切换页），输入框跟着同步
  useEffect(() => setDraft(value), [value]);
  return (
    <form
      className={s.search}
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(draft.trim() || undefined);
      }}
    >
      <Icon name="srch" />
      <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={placeholder} enterKeyHint="search" aria-label={placeholder} />
      {draft && (
        <button type="button" className={s.clearQ} aria-label="清除搜索" onClick={() => { setDraft(''); onSubmit(undefined); }}>
          <Icon name="x" size={14} />
        </button>
      )}
    </form>
  );
}

export type ChipItem = { key: string; label: string; count?: number };

/** 视图 chips：横滑，带计数；count 缺省时不显示数字 */
export function ViewChips({ items, value, onChange }: { items: ChipItem[]; value: string; onChange: (key: string) => void }) {
  const box = useRef<HTMLDivElement>(null);
  // 选中的 chip 可能在横滑条的屏幕外（例如从链接直接进来），滚到可见位置
  useEffect(() => {
    box.current?.querySelector(`.${s.on}`)?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [value, items.map((i) => i.count).join()]); // eslint-disable-line react-hooks/exhaustive-deps -- 计数到了 chip 会变宽，要再滚一次
  return (
    <div className={s.chips} ref={box}>
      {items.map((v) => (
        <button key={v.key} type="button" className={`${s.chip} ${v.key === value ? s.on : ''}`} onClick={() => onChange(v.key)} aria-pressed={v.key === value}>
          {v.label}
          {v.count != null && <i className="tnum">{v.count}</i>}
        </button>
      ))}
    </div>
  );
}

export function FilterButton({ count, onClick }: { count: number; onClick: () => void }) {
  return (
    <button type="button" className={`${s.fbtn} ${count ? s.fon : ''}`} onClick={onClick}>
      <Icon name="filter" />筛选{count > 0 && <span className={s.fbadge}>{count}</span>}
    </button>
  );
}

/** 已选条件回显：直接写值，点一下去掉（文档 23 §3.4） */
export function AppliedChips({ items, onRemove, onClear }: { items: { key: string; label: string }[]; onRemove: (key: string) => void; onClear: () => void }) {
  if (!items.length) return null;
  return (
    <div className={s.applied}>
      {items.map((it) => (
        <button key={it.key} type="button" className={s.achip} onClick={() => onRemove(it.key)}>
          {it.label}<Icon name="x" size={14} />
        </button>
      ))}
      <button type="button" className={s.clr} onClick={onClear}>清空</button>
    </div>
  );
}

/** 页内切换：产品 / 套装 / 画册、客户 / 供应商（是「页」不是视图，所以用路由） */
export function Segments({ items }: { items: { to: string; label: string }[] }) {
  if (items.length < 2) return null;
  return (
    <nav className={s.seg} aria-label="切换">
      {items.map((i) => (
        <NavLink key={i.to} to={i.to} end className={({ isActive }) => (isActive ? s.on : '')}>
          {i.label}
        </NavLink>
      ))}
    </nav>
  );
}

export function Pager({ page, size, count, total, hasNext, onGo }: { page: number; size: number; count: number; total: number; hasNext: boolean; onGo: (p: number) => void }) {
  return (
    <footer className={s.pager}>
      <span className="tnum">{total ? `第 ${(page - 1) * size + 1}–${(page - 1) * size + count} 条 / 共 ${total} 条` : '共 0 条'}</span>
      <span className={s.sp} />
      <Button size="sm" disabled={page <= 1} onClick={() => onGo(page - 1)}>上一页</Button>
      <span className="tnum">{page}</span>
      <Button size="sm" disabled={!hasNext} onClick={() => onGo(page + 1)}>下一页</Button>
    </footer>
  );
}

type BodyState = { loading: boolean; error?: Error; count: number };

/** 列表主体的四态：失败 / 首次加载 / 空 / 有数据 */
export function ListBody({ state, errorTitle, detail, onRetry, empty, children, skeleton = 3 }: {
  state: BodyState; errorTitle: string; detail: string; onRetry: () => void; empty: ReactNode; children: ReactNode; skeleton?: number;
}) {
  if (state.error && !state.count) return <ErrorState title={errorTitle} message={state.error.message} detail={detail} onRetry={onRetry} />;
  if (state.loading && !state.count) return <div className={s.pad}>{Array.from({ length: skeleton }, (_, i) => <Skeleton key={i} lines={i % 2 ? 3 : 4} />)}</div>;
  if (!state.count) return <>{empty}</>;
  return <>{children}</>;
}

/** 卡片列表外壳：计数行 + 卡片 + 触底加载提示 */
export function CardList({ bar, hasNext, loading, count, children, sentinel }: {
  bar: ReactNode; hasNext: boolean; loading: boolean; count: number; children: ReactNode; sentinel?: (el: HTMLDivElement | null) => void;
}) {
  return (
    <div className={s.cards}>
      <div className={s.listbar}>{bar}</div>
      {children}
      <div ref={sentinel} className={s.more}>{hasNext ? (loading ? '加载中…' : '上滑加载更多') : count > 0 ? '没有更多了' : ''}</div>
    </div>
  );
}

export { Empty };

/* ---------------- 骨架 ---------------- */

type ScaffoldProps = {
  title: string;
  total?: number;
  segments?: ReactNode;
  chips?: ReactNode;
  filterBtn?: ReactNode;
  /** 手机分类 chips 行末尾的搜索入口，与页头的全局搜索分开。 */
  searchAction?: boolean;
  applied?: ReactNode;
  search: ReactNode;
  searching?: boolean;
  /** 新建入口可指向 V2 本地表单或老版页面。 */
  create?: { href: string; label: string; local?: boolean } | null;
  actions?: ReactNode;
  onRefresh?: () => void;
  /** 手机 / iPad 列表栏的内容（卡片） */
  list: ReactNode;
  /** 桌面主区（表格）；不给就用 list */
  desk?: ReactNode;
  pager?: ReactNode;
  sidebar?: ReactNode;
  subtitle?: ReactNode;
  /** iPad 分栏的右侧；不给则 iPad 用桌面版式 */
  detail?: ReactNode;
  overlay?: ReactNode;
};

export function ListScaffold(p: ScaffoldProps) {
  const ff = useFormFactor();
  const [searching, setSearching] = useState(!!p.searching);
  const create = p.create && (p.create.local
    ? <NavLink to={p.create.href} className={s.newBtn}><Icon name="plus" />{p.create.label}</NavLink>
    : <a href={p.create.href} className={s.newBtn} title="只读体验期：在老版中操作"><Icon name="plus" />{p.create.label}</a>);

  if (ff === 'phone') {
    return (
      <div className={s.phone}>
        <header className={s.pnb}>
          <h1>{p.title}</h1>
          <IconButton name="srch" label="搜索" onClick={() => setSearching((x) => !x)} />
          {p.actions}
          {p.create && (p.create.local ? <NavLink className={s.pIcon} to={p.create.href} aria-label={p.create.label}><Icon name="plus" size={22} /></NavLink> : <a className={s.pIcon} href={p.create.href} aria-label={`${p.create.label}（老版）`}><Icon name="plus" size={22} /></a>)}
        </header>
        {searching && <div className={s.psearch}>{p.search}</div>}
        {p.segments && <div className={s.segbar}>{p.segments}</div>}
        {(p.chips || p.filterBtn || p.searchAction) && <div className={s.chipbar}>{p.chips || <span className={s.sp} />}{p.searchAction ? <button type="button" className={s.searchAction} onClick={() => setSearching(true)}><Icon name="srch" size={16} />搜索</button> : p.filterBtn}</div>}
        {p.applied}
        <div className={s.pbody}>{p.list}</div>
        {p.overlay}
      </div>
    );
  }

  if (ff === 'tablet' && p.detail !== undefined) {
    return (
      <div className={s.split}>
        <section className={s.listCol}>
          <header className={s.lh}>
            <h1>{p.title}{p.total != null && <small className="tnum">{p.total}</small>}</h1>
            {create}
            {p.actions}
          </header>
          {p.segments && <div className={s.lseg}>{p.segments}</div>}
          <div className={s.lsearch}>{p.search}{p.filterBtn}</div>
          {p.chips && <div className={s.lchips}>{p.chips}</div>}
          {p.applied}
          <div className={s.lbody}>{p.list}</div>
        </section>
        <div className={s.divider} />
        <section className={s.detailCol}>{p.detail}</section>
        {p.overlay}
      </div>
    );
  }

  if (ff === 'desktop' && p.sidebar) {
    return <div className={s.desktopPage}>
      <header className={s.desktopHead}>
        <div><h1>{p.title}</h1><p>{p.subtitle}{p.total != null && <span className="tnum">共 {p.total} 条</span>}</p></div>
        <div className={s.desktopActions}>{p.segments}{p.onRefresh && <Button icon="ref" onClick={p.onRefresh}>刷新</Button>}{p.actions}{create}</div>
      </header>
      <div className={s.desktopWork}>
        {p.sidebar}
        <section className={s.desktopArea}>
          <div className={s.desktopToolbar}>{p.search}{p.filterBtn}</div>
          {p.applied}
          <div className={s.dbody}>{p.desk ?? p.list}</div>
          {p.pager}
        </section>
      </div>
      {p.overlay}
    </div>;
  }

  return (
    <div className={s.desk}>
      <header className={s.dh}>
        {p.subtitle ? <div className={s.dhTitle}><h1>{p.title}{p.total != null && <small className="tnum">共 {p.total} 条</small>}</h1><p>{p.subtitle}</p></div> : <h1>{p.title}{p.total != null && <small className="tnum">共 {p.total} 条</small>}</h1>}
        {p.segments && <div className={s.dseg}>{p.segments}</div>}
        <span className={s.sp} />
        {p.search}
        {p.filterBtn}
        {p.onRefresh && <Button icon="ref" onClick={p.onRefresh}>刷新</Button>}
        {create}
        {p.actions}
      </header>
      {p.chips && <div className={s.dchips}>{p.chips}</div>}
      {p.applied}
      <div className={s.dbody}>{p.desk ?? p.list}</div>
      {p.pager}
      {p.overlay}
    </div>
  );
}
